import { useNavigate, useSearchParams } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { DomainReportsTab } from '../components/reports'
import { ActivationTab } from './license-management/ActivationTab'
import { CriteriaTab } from './license-management/CriteriaTab'
import { LicensesTab } from './license-management/LicensesTab'
import { TabNavigation } from './license-management/TabNavigation'
import type { TabId } from './license-management/helpers'
import {
  useLicenseManagementData,
  type LicenseManagementData,
} from './license-management/use-license-management-data'

interface PageHeaderProps {
  loading: boolean
  onRefresh: () => void
}

function PageHeader({ loading, onRefresh }: PageHeaderProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--text)]">License Management</h1>
        <p className="mt-1 text-sm text-[var(--text-dim)]">
          Manage licenses, criteria, and activation workflow
        </p>
      </div>
      <button
        onClick={onRefresh}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--text-dim)] transition-colors hover:bg-[var(--accent-dim)] disabled:opacity-50"
      >
        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  )
}

interface TabBodyProps {
  activeTab: TabId
  data: LicenseManagementData
  onCreateOrganisation: (xeroOrgId?: string) => void
}

function TabBody({ activeTab, data, onCreateOrganisation }: TabBodyProps): React.ReactElement {
  if (activeTab === 'licenses') {
    return (
      <LicensesTab
        licenses={data.licenses}
        organisations={data.organisations}
        xeroInvoices={data.xeroInvoices}
        licenseCriteria={data.criteria}
        onRefresh={data.reload}
      />
    )
  }
  if (activeTab === 'criteria') {
    return (
      <CriteriaTab
        criteria={data.criteria}
        organisations={data.organisations}
        xeroInvoices={data.xeroInvoices}
        onRefresh={data.reload}
      />
    )
  }
  if (activeTab === 'activation') {
    return (
      <ActivationTab
        entitlements={data.entitlements}
        xeroInvoices={data.xeroInvoices}
        xeroStates={data.xeroStates}
        organisations={data.organisations}
        onRefresh={data.reload}
        onCreateOrganisation={onCreateOrganisation}
      />
    )
  }
  return <DomainReportsTab domain="licensing" />
}

function LoadingView(): React.ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[var(--accent)]" />
    </div>
  )
}

interface ErrorViewProps {
  error: string
  onRetry: () => void
}

function ErrorView({ error, onRetry }: ErrorViewProps): React.ReactElement {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <p className="text-red-600 dark:text-red-400">{error}</p>
      <button
        onClick={onRetry}
        className="mt-2 text-sm text-red-700 underline hover:no-underline dark:text-red-300"
      >
        Try again
      </button>
    </div>
  )
}

export function LicenseManagementPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const activeTab = (searchParams.get('tab') as TabId | null) ?? 'licenses'
  const data = useLicenseManagementData()

  const handleTabChange = (tab: TabId): void => {
    setSearchParams({ tab })
  }
  const handleRefresh = (): void => {
    void data.reload()
  }
  const handleCreateOrganisation = (xeroOrgId?: string): void => {
    if (xeroOrgId) {
      void navigate(`/admin/organisations?xeroOrgId=${encodeURIComponent(xeroOrgId)}`)
    } else {
      void navigate('/admin/organisations')
    }
  }

  if (data.loading) return <LoadingView />
  if (data.error) return <ErrorView error={data.error} onRetry={handleRefresh} />

  return (
    <div className="space-y-6">
      <PageHeader loading={data.loading} onRefresh={handleRefresh} />
      <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      <TabBody activeTab={activeTab} data={data} onCreateOrganisation={handleCreateOrganisation} />
    </div>
  )
}
